// © Microsoft Corporation. All rights reserved.

import { AccentButton, Button, NeutralButton, OutlineButton } from "@microsoft/fast-components-react-msft";
import {
    ProductType,
    getSubscriptionStatus,
    renderCreateSubscription,
    renderLinkToPublisher,
    renderManageSubscription
} from "@msnews/subscription-sdk-wrapper";
import {PublisherProfileCardTelemetryObjects, publisherProfileCardTelemetryContracts} from "./PublisherProfileCard.telemetry";
import { SharingHelper, SocialMedia } from "@msnews/views-helpers";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";
import { check, email, facebook, linkedin, plus, publisherTabPremium, twitter } from "../../utils/icons";

import Popover from "../../common/popover/Popover";
import PublisherProfileCardStats from "./publisher-profile-card-stats/PublisherProfileCardStats";
import React from "react";
import { SocialProps } from "../../Social.props";
import avatarFallback from "../../utils/avatarFallback";
import { classNames } from "@microsoft/fast-web-utilities";
import { enums } from "../../utils/enums";
import { getContextData } from "@msnews/core";
import { getExternalURL } from "../../utils/urlHelper";
import { getXPayToken } from "@msnews/social-data-service";
import { placeholderImages } from "../../utils/images";

export interface PublisherProfileCardProps extends SocialProps {
    emptyState?: string;
}
export interface PublisherProfileCardState {
    isSubscribed: boolean;
    showCannotUnfollowNotification: boolean;
    alreadySubscribedPopoverVisible: boolean;
}

export default class PublisherProfileCard extends React.Component<PublisherProfileCardProps, PublisherProfileCardState> {

    alreadySubscribedTriggerRef: React.RefObject<HTMLDivElement>;
    activeSharePlatforms: SocialMedia[] = [SocialMedia.Facebook, SocialMedia.Twitter, SocialMedia.LinkedIn, SocialMedia.Email];
    telemetryObjects: PublisherProfileCardTelemetryObjects;

    constructor(props) {
        super(props);
        this.state = {
            isSubscribed: false,
            showCannotUnfollowNotification: false,
            alreadySubscribedPopoverVisible: false
        };
        this.telemetryObjects = null;
        this.alreadySubscribedTriggerRef = React.createRef();
    }

    componentDidMount() {
        this.checkSubscriptionStatus();
    }

    render() {
        const { responsiveClass, users, managedClasses, mainTelemetryObject } = this.props;
        const { isSubscribed } = this.state;

        const publisherId = this.props.id;
        const publisher = users && (users[publisherId] ? users[publisherId].data : null);
        if (!publisher) { return null; }
        const { isFollowing } = publisher;

        const publisherProfileCardTelemetryObject = addOrUpdateTelemetryObject(mainTelemetryObject, publisherProfileCardTelemetryContracts.publisherProfileCard, {}, this.telemetryObjects && this.telemetryObjects.publisherProfileCard);
        this.telemetryObjects = {
            publisherProfileCard: publisherProfileCardTelemetryObject,
            subscribeButton: addOrUpdateTelemetryObject(publisherProfileCardTelemetryObject, publisherProfileCardTelemetryContracts.subscribeButton, {headline: isSubscribed ? "Subscribe" : "Unsubscribe"}, this.telemetryObjects && this.telemetryObjects.subscribeButton),
            followButton: addOrUpdateTelemetryObject(publisherProfileCardTelemetryObject, publisherProfileCardTelemetryContracts.followButton, {headline: isFollowing ? "Follow" : "Unfollow"}, this.telemetryObjects && this.telemetryObjects.followButton)
        };

        return (
            <div
                role="main"
                className={classNames(
                    managedClasses.publisherProfileCardContainer,
                    responsiveClass
                )}
                {...renderHybrid(this.telemetryObjects && this.telemetryObjects.publisherProfileCard, true)}
            >
                <div className="cwt-box user-box">
                    <div className="cwt-user-info-container"> { this.renderPublisherInfo(publisher, isFollowing, isSubscribed) }</div>
                </div>
                <div className="cwt-box subscribe-box">
                    <div className="cwt-action-panel-container"> { this.renderActionPanel() } </div>
                </div>
                <div className="cwt-box social-box">
                    <div className="cwt-about-panel-container"> {this.renderAboutPanel() } </div>
                    <div className="cwt-social-panel-container"> {this.renderSocialPanel()} </div>
                </div>
            </div>
        );
    }

    renderPublisherInfo(publisher, isFollowing, isSubscribed) {
        const { managedClasses, t, emptyState} = this.props;
        const { followersCount, likesCount, primaryAvatar, primaryName } = publisher;
        const avatarUrl = emptyState == enums.profilePageEmptyStates.NotSocialUser ? placeholderImages.notSocialAvatar : primaryAvatar || placeholderImages.avatar;
        const followButtonText = isFollowing || isSubscribed ? t.PublisherProfilePageFollowing : t.PublisherProfilePageFollow;

        return  (
            <React.Fragment>
                <div className="cwt-user-info cwt-inner-container">
                    <div className="cwt-user-header">
                        <div className="cwt-avatar-container" title={""}>
                            <img src={avatarUrl} alt={t.AriaLabelsProfilePicture} className="cwt-avatar" onError={(e)=>{ avatarFallback(e); }}/>
                        </div>
                        <div className={classNames("cwt-name-title")}>
                            <div className="cwt-name">{primaryName}</div>
                            <PublisherProfileCardStats {...{ ...this.props, followersCount, likesCount}} />
                        </div>
                    </div>
                </div>
                <div
                    className="cwt-button-container cwt-section cwt-inner-container"
                    ref={this.alreadySubscribedTriggerRef}
                    onMouseEnter={(e) => this.setState({alreadySubscribedPopoverVisible: true})}
                    onMouseLeave={(e) => this.setState({alreadySubscribedPopoverVisible: false})}
                >
                    <OutlineButton
                        disabled={this.state.isSubscribed}
                        beforeContent={() => <i className="cwt-icon">{ isFollowing || isSubscribed ? check : plus }</i> }
                        className={classNames(
                            "cwt-fast-button",
                            ["outline-button-accent", !isFollowing && !isSubscribed]
                        )}
                        onClick={this.handleFollowing.bind(this)}
                        {...renderHybrid(this.telemetryObjects && this.telemetryObjects.followButton, false)}
                    >{followButtonText}
                    </OutlineButton>
                </div>
                { this.state.alreadySubscribedPopoverVisible && this.state.isSubscribed && <Popover
                    {...{
                        visible: this.state.alreadySubscribedPopoverVisible && this.state.isSubscribed,
                        triggerRef: this.alreadySubscribedTriggerRef,
                        onHide: () => this.setState({alreadySubscribedPopoverVisible: false}),
                        className: managedClasses.alreadySubscribedPopoverContainer,
                        managedClasses: managedClasses,
                        isHover: true,
                        hoverDelay: 600
                    }}>
                    <div className={managedClasses.infoPopover}><p>{ t.PublisherProfilePagePaidSubscriptionUnfollowBody }</p></div>
                </Popover>}
            </React.Fragment>
        );
    }

    renderActionPanel() {
        const { t, managedClasses, usersConnector } = this.props;
        const userId = this.props.id;
        const isSubscribed = this.state.isSubscribed;
        const subscribeButtonText = isSubscribed ? t.PublisherProfilePageSubscribed :t.PublisherProfilePageSubscribe;

        const SubscribeButtonWrapperComponent = isSubscribed ? OutlineButton : AccentButton;

        return (
            <React.Fragment>
                <div className="action-panel-header cwt-inner-container">
                    <h4 className="cwt-title">{t.ProfilePageSubscriptionsUnlockBenefits}</h4>
                </div>
                <div className="cwt-action-panel cwt-section cwt-inner-container">
                    <div className="cwt-button-container">
                        <SubscribeButtonWrapperComponent
                            className={ classNames(
                                "cwt-fast-button",
                                ["button-inactive", !isSubscribed]
                            )}
                            onClick={this.handleSubscription.bind(this)}
                            beforeContent={() => <i className="cwt-icon">{publisherTabPremium}</i> }
                            {...renderHybrid(this.telemetryObjects && this.telemetryObjects.subscribeButton, false)}>
                            { subscribeButtonText }
                        </SubscribeButtonWrapperComponent>
                    </div>
                    { !this.state.isSubscribed && <div className="cwt-action-panel-text-container">
                        <a className="cwt-link" onClick={this.handleAlreadySubscribedClick.bind(this)} href={null}> { t.PublisherProfilePageAlreadySubscribedQuestion } </a>
                    </div> }
                </div>
            </React.Fragment>
        );
    }

    renderAboutPanel() {
        const { t, users, id } = this.props;
        const publisher = users[id];
        const aboutText = publisher.data.otherProperties && publisher.data.otherProperties.about;
        return (
            <React.Fragment>
                <div className="cwt-inner-container"><h4 className="cwt-title"> { t.PublisherProfilePageAboutUs } </h4></div>
                <div className="cwt-section cwt-inner-container"><p className="cwt-about-text"> {aboutText} </p></div>
            </React.Fragment>
        );
    }

    renderSocialPanel() {
        const { t } = this.props;
        const icons = {
            email, facebook, linkedin, twitter
        };
        return (
            <div className="cwt-social cwt-section cwt-inner-container">
                <span className="cwt-share-text"> { t.PublisherProfilePageShareTo } </span>
                <div className="cwt-share-buttons">
                    {
                        this.activeSharePlatforms.map((platform) => (
                            <Button key={platform} className="cwt-fast-icon-button" onClick={(e) => this.share(e, platform)}>
                                <i className="cwt-icon">{icons[platform]}</i>
                            </Button>
                        ))
                    }
                </div>
            </div>
        );
    }

    async checkSubscriptionStatus() {
        const { me } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (!isAnonymous) {
            getSubscriptionStatus({
                publisherId: this.props.id,
                authToken: await getXPayToken()
            }).then(response => {
                if (response.status && response.data) {
                    this.setState(() => ({ isSubscribed: true }));
                }
            });
        }
    }

    async handleAlreadySubscribedClick() {
        const { id, users, me, market } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) {
            this.signIn();
        } else {
            const publisher = users[id] ? users[id].data : null;
            const { primaryAvatar, primaryName } = publisher;
            renderLinkToPublisher({
                publisherId: id,
                publisherName: primaryName,
                publisherLogoUrl: primaryAvatar,
                userProfilePageUrl: getExternalURL(`${enums.externalUrls.profilePage}${me.id}`, market),
                userProfileImageUrl: me.primaryAvatar,
                locale: market,
                authToken: await getXPayToken(),
                onSuccess: () => window.location.reload()
            });
        }
    }

    handleSubscription() {
        const { me } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) { this.signIn(); return; }
        this.state.isSubscribed ? this.manageSubscription() : this.subscribeToPublisher();
    }

    handleFollowing() {
        const { me, id, users } = this.props;
        const isAnonymous = me && me.state == enums.userStateType.anonymous;
        if (isAnonymous) { this.signIn(); return; }
        if (this.state.isSubscribed) {
            this.setState({showCannotUnfollowNotification: true});
            return;
        }
        const publisher = users[id] ? users[id].data : null;
        this.props.usersConnector.followUser(id, !publisher.isFollowing);
    }

    async subscribeToPublisher() {
        const { id, users, me, market } = this.props;
        const publisher = users[id] ? users[id].data : null;
        const { primaryAvatar, primaryName } = publisher;

        renderCreateSubscription({
            publisherId: id,
            publisherName: primaryName,
            publisherLogoUrl: primaryAvatar,
            productType: ProductType.Premium,
            userProfilePageUrl: getExternalURL(`${enums.externalUrls.profilePage}${me.id}`, market),
            locale: market,
            authToken: await getXPayToken(),
            userProfileImageUrl: me.primaryAvatar,
            scenario: "feed",
            onSuccess: () => {
                this.props.usersConnector.followUser(id, true);
                window.location.reload();
            }
        });
    }

    async manageSubscription() {
        const { id, users, me, market } = this.props;
        const publisher = users[id] ? users[id].data : null;
        const { primaryAvatar, primaryName } = publisher;

        renderManageSubscription({
            publisherId: id,
            publisherName: primaryName,
            publisherLogoUrl: primaryAvatar,
            userProfilePageUrl: getExternalURL(`${enums.externalUrls.profilePage}${me.id}`, market),
            locale: market,
            authToken: await getXPayToken(),
            userProfileImageUrl: me.primaryAvatar,
            onSuccess: () => window.location.reload()
        });
    }

    signIn() {
        this.props.popupConnector.openPopup("multiLoginPage");
    }

    share(e, platform: SocialMedia) {
        const title = "Document Title";
        const requestContext = this.props.requestContext;
        const pageUrl = "msn.com";
        const locationHref = getContextData(requestContext.data.locationHref, "locationHref");
        const shareUrl = SharingHelper.getSocialMediaShareUrl(locationHref, platform, pageUrl, title);
        SharingHelper.openNewWindow(e, shareUrl);
    }

}